import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store, select, Action } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { map, withLatestFrom, switchMap, catchError, filter } from 'rxjs/operators';
import {
  OrderBuilderState,
  getSelectedBusinessLine,
  getJobsiteSelected,
  getJobsiteSalesAreas,
} from '@ordersnproducts/modules/order-builder/reducers/index';
import * as fromExperiencesActions from '@ordersnproducts/modules/order-builder/modules/location/actions/experiences-actions/experiences.actions';
import {
  ExperiencesService,
  ExperiencesRequest,
  FullSalesArea,
  COUNTRY_CODE,
  ExperiencesResponse,
} from '@cemex-core/cmx-api';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';
import { ACTION_TYPES, ORDER_ACTIONS } from '@ordersnproducts/modules/shared/constants/order-actions';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { CmxHttpClient, getLegalEntity } from '@cemex/cmx-client-layout';
import {
  LoadJobsiteFullSalesAreaComplete,
  LoadJobsiteFullSalesAreaFail,
  LoadJobsiteSalesAreaComplete,
  LOAD_SALES_AREA_FAIL,
  LOAD_SALES_AREA_SUCCESS,
} from '../../actions/jobsite-actions/jobsite.actions';

@Injectable()
export class ExperiencesLoadEffect {
  listenJobsiteSalesArea$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_SALES_AREA_SUCCESS),
      filter((action: LoadJobsiteSalesAreaComplete) => !!action.payload),
      switchMap(() => {
        return of(new fromExperiencesActions.Fetch());
      }),
    ),
  );

  jobsiteSalesAreaFail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_SALES_AREA_FAIL),
      switchMap(() => {
        this.store$.dispatch(new LoadJobsiteFullSalesAreaFail(null));
        return of(new fromExperiencesActions.FetchFailure());
      }),
    ),
  );

  fetch$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<fromExperiencesActions.Fetch>(fromExperiencesActions.ActionTypes.FETCH),
      withLatestFrom(
        this.store$.pipe(select(getSelectedBusinessLine)),
        this.store$.pipe(select(getJobsiteSelected)),
        this.store$.pipe(select(getJobsiteSalesAreas)),
        this.store$.pipe(select(getLegalEntity)),
      ),
      filter(
        ([action, businessLine, jobsite, jobsiteSalesAreas, customer]) =>
          !!businessLine && !!jobsite && !!jobsiteSalesAreas && !!customer,
      ),
      switchMap(([action, businessLine, jobsite, jobsiteSalesAreas, customer]) => {
        console.log('[ModifiedFields] Fetching experience for customer:', {
          customerCode: customer?.legalEntityCode,
          customerName: customer?.legalEntityDesc,
          countryCode: customer?.countryCode?.trim(),
        });
        const requestParams = this.makeCustomerAndCountryCodeFromCustomer(businessLine, jobsite, customer);
        const fullSalesAreaLink = _safe(() => jobsiteSalesAreas[0].salesArea.links.self);

        if (fullSalesAreaLink) {
          return forkJoin([
            this.experiencesApi.getExperiences(requestParams),
            this.http.get<FullSalesArea>(fullSalesAreaLink),
          ]).pipe(
            map(([experiencesResp, fullSalesAreaResp]) => {
              const expCode = _safe(() => (experiencesResp as ExperiencesResponse).experiences[0].experienceCode);
              const salesOrg = _safe(() => fullSalesAreaResp.salesOrganizationCode);
              if (
                (!!expCode && !!salesOrg && Object.values(ORDER_EXPERIENCES).includes(expCode as ORDER_EXPERIENCES)) ||
                customer.countryCode.trim() === COUNTRY_CODE.ISRAEL
              ) {
                this.store$.dispatch(new LoadJobsiteFullSalesAreaComplete(fullSalesAreaResp));
                return new fromExperiencesActions.FetchSuccess(experiencesResp as ExperiencesResponse);
              }

              this.store$.dispatch(new LoadJobsiteFullSalesAreaFail(null));
              return new fromExperiencesActions.FetchFailure();
            }),
            catchError((error) => {
              this.store$.dispatch(new LoadJobsiteFullSalesAreaFail(error));
              return of(new fromExperiencesActions.FetchFailure());
            }),
          );
        }

        this.store$.dispatch(new LoadJobsiteFullSalesAreaFail(null));
        return of(new fromExperiencesActions.FetchFailure());
      }),
    ),
  );

  fetchSucess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<fromExperiencesActions.FetchSuccess>(fromExperiencesActions.ActionTypes.FETCH_SUCCESS),
      withLatestFrom(this.store$.pipe(select(getLegalEntity))),
      map(([action, customer]) => {
        const payload = action.payload;
        if (customer.countryCode.trim() === COUNTRY_CODE.ISRAEL) {
          console.log('[ModifiedFields] Experience resolved for customer:', {
            customerCode: customer?.legalEntityCode,
            customerName: customer?.legalEntityDesc,
            experienceCode: ORDER_EXPERIENCES.SIMMA_UNIVERSAL_LINK,
            isCommand: false,
          });
          return new fromExperiencesActions.SetExperience({
            experienceCode: ORDER_EXPERIENCES.SIMMA_UNIVERSAL_LINK,
            _links: [],
            experienceId: 0,
            isOnline: 0,
            features: [{ key: 'ValueAddedSpecialized', enabled: false }],
          });
        } else {
          const resolvedExperience = payload.experiences[0].experienceCode;
          console.log('[ModifiedFields] Experience resolved for customer:', {
            customerCode: customer?.legalEntityCode,
            customerName: customer?.legalEntityDesc,
            experienceCode: resolvedExperience,
            isCommand: resolvedExperience === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK,
          });
          return new fromExperiencesActions.SetExperience({
            _links: [],
            experienceId: payload.experiences[0].experienceId,
            isOnline: payload.experiences[0].isOnline,
            experienceCode: payload.experiences[0].experienceCode,
            features: payload.experiences[0].features,
          });
        }
      }),
    ),
  );

  fetchFailure$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<fromExperiencesActions.FetchFailure>(fromExperiencesActions.ActionTypes.FETCH_FAILURE),
      map(() => {
        return new fromExperiencesActions.SetExperience({
          _links: [],
          experienceId: 1,
          isOnline: 0,
          experienceCode: ORDER_EXPERIENCES.REQUEST,
        });
      }),
    ),
  );

  private makeCustomerAndCountryCodeFromCustomer(businessLine, jobsite, customer): ExperiencesRequest {
    if (customer.countryCode.trim() === COUNTRY_CODE.ISRAEL) {
      return {
        productTypeCode: businessLine.productTypeCode,
        actionType: ACTION_TYPES.ORDER,
        actionCode: ORDER_ACTIONS.CREATE,
        countryCode: customer.countryCode.trim(),
      };
    } else {
      // For MX, we need to send only the first product type code when BAG CEMENT/MULTIPRODUCT
      const splitedProductTypeCode = businessLine.productTypeCode.split(',')[0];
      return {
        jobsiteCode: jobsite.shipmentLocationCode,
        productTypeCode: splitedProductTypeCode,
        actionType: ACTION_TYPES.ORDER,
        actionCode: ORDER_ACTIONS.CREATE,
      };
    }
  }
  constructor(
    private actions$: Actions,
    private store$: Store<OrderBuilderState>,
    private http: CmxHttpClient,
    private experiencesApi: ExperiencesService,
  ) {}
}
